import type { Character, MigrationRemoved, SnapshotMigration } from "../character";
import { reopenedItemId, unseenMigrations } from "../migration";
import { Modal } from "./modal";
import { useSheetActions } from "./sheet-context";

interface Props {
  character: Character;
  busy: boolean;
  /** Dismiss the report: the records are marked seen and saved. */
  onDismiss: () => void;
  /** Open the Builder on a reopened Choice (its guide item id). */
  onOpenChoice: (itemId: string) => void;
}

const REMOVED_LABELS: Record<MigrationRemoved["kind"], string> = { grant: "Granted Selection", spell: "Spell", item: "Inventory item", condition: "Condition", effect: "Effect", consumable: "Consumable" };

/**
 * The migration report (issue #17): shown once on the Character's next open after a Snapshot
 * migration. Lists what the redirects replaced, every Choice reopened with the Entry it held,
 * linking into the Builder, and everything removed because its Entry left the Snapshot.
 */
export function MigrationReport({ character, busy, onDismiss, onOpenChoice }: Props) {
  const records = unseenMigrations(character);
  if (records.length === 0) return null;
  return (
    <Modal title="Snapshot updated" onClose={onDismiss} class="migration-report">
      {records.map((m) => <Record key={`${m.from}>${m.to}@${m.at}`} record={m} onOpenChoice={onOpenChoice} />)}
      <span class="actions">
        <button type="button" class="primary" disabled={busy} onClick={onDismiss}>Got it</button>
      </span>
    </Modal>
  );
}

function Record({ record, onOpenChoice }: { record: SnapshotMigration; onOpenChoice: (itemId: string) => void }) {
  const { openEntry } = useSheetActions();
  return (
    <section class="migration-record">
      <p>
        <strong>{record.from}</strong> to <strong>{record.to}</strong>
        <span class="muted"> · {new Date(record.at).toLocaleDateString()}</span>
      </p>
      <p>
        {record.redirected === 0 ? "No UUID needed a redirect" : `${record.redirected} ${record.redirected === 1 ? "UUID" : "UUIDs"} redirected to ${record.redirected === 1 ? "its" : "their"} new Entry`}
        {record.reopened.length === 0 && record.removed.length === 0 ? "; nothing else changed." : "."}
      </p>
      {record.reopened.length > 0 && (
        <>
          <h3>{record.reopened.length === 1 ? "One Choice reopened" : `${record.reopened.length} Choices reopened`}</h3>
          <ul class="plain">
            {record.reopened.map((r) => (
              <li key={reopenedItemId(r)}>
                <div class="item-row">
                  <span class="item-label">
                    Level {r.level}: {r.label}
                    <span class="muted"> · {r.reason === "missing" ? <>{r.name ?? r.uuid ?? "the Entry"} is no longer in the Snapshot</> : <>{r.name ?? r.uuid ?? "the Selection"}: {r.detail ?? "no longer legal"}</>}</span>
                    {r.reason === "missing" && r.uuid && r.name ? <span class="muted"> ({r.uuid})</span> : null}
                  </span>
                  <span class="actions">
                    {r.reason === "illegal" && r.uuid ? <button type="button" onClick={() => openEntry(r.uuid!)}>Read</button> : null}
                    <button type="button" class="primary" onClick={() => onOpenChoice(reopenedItemId(r))}>Choose again</button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {record.removed.length > 0 && (
        <>
          <h3>Removed</h3>
          <ul class="plain">
            {record.removed.map((r, i) => (
              <li key={`${r.kind}:${r.uuid}:${i}`}>
                <span class="tag">{REMOVED_LABELS[r.kind]}</span> {r.name ?? r.uuid}
                {r.name ? <span class="muted"> ({r.uuid})</span> : null}
                {r.detail ? <span class="muted"> · {r.detail}</span> : null}
              </li>
            ))}
          </ul>
          <p class="muted">Removed Entries are not in this Snapshot, or their granter no longer grants them.</p>
        </>
      )}
    </section>
  );
}
